import React from 'react';
import ProfileAssistantRow from '../../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../../GeneralProfile/ProfileUserAnswerRow';
import type { RetirementAllowanceFlow } from './useRetirementAllowanceFlow';
import type { RetirementAllowanceEntry, RetirementAllowanceStep } from './retirementAllowanceTypes';
import { entryHasAmount } from './retirementAllowanceSteps';

function AnswerRow({
  flow,
  fieldStep,
  children,
}: {
  flow: RetirementAllowanceFlow;
  fieldStep: RetirementAllowanceStep;
  children: React.ReactNode;
}) {
  return (
    <ProfileUserAnswerRow
      showUndo={flow.canShowUndoForStep(fieldStep)}
      onUndo={() => void flow.handleUndo(fieldStep)}
      undoLabel={flow.undoLabel}
    >
      {children}
    </ProfileUserAnswerRow>
  );
}

export default function RetirementAllowanceChatThread({
  flow,
  embedInOtherClaims = false,
}: {
  flow: RetirementAllowanceFlow;
  embedInOtherClaims?: boolean;
}) {
  const { t, snap, step } = flow;
  const inDependentLoop =
    snap.recipientCategory === 'dependents' &&
    !flow.reviewMode &&
    (step === 'pickDependent' || step === 'addAnotherDependent');

  return (
    <>
      {!embedInOtherClaims ? (
        <>
          <ProfileAssistantRow>{t(flow.stepQuestionKey('intro'))}</ProfileAssistantRow>
          <ProfileAssistantRow>
            {t('submitDocuments.dependentDeduction.retirementAllowance.introExamples')}
          </ProfileAssistantRow>
          <ProfileAssistantRow>
            {t('submitDocuments.dependentDeduction.retirementAllowance.notSalaryNote')}
          </ProfileAssistantRow>
        </>
      ) : null}

      {flow.isCommitted('gate') && snap.anyExpected !== '' ? (
        <>
          <ProfileAssistantRow>{flow.gateQuestion()}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="gate">
            {flow.userAnswerForStep('gate')}
          </AnswerRow>
        </>
      ) : null}

      {flow.isCommitted('recipientType') && snap.recipientCategory !== '' ? (
        <>
          <ProfileAssistantRow>{t(flow.stepQuestionKey('recipientType'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="recipientType">
            {flow.userAnswerForStep('recipientType')}
          </AnswerRow>
        </>
      ) : null}

      {snap.entries.map((entry) => (
        <React.Fragment key={entry.empFamilyId}>
          <ProfileAssistantRow>
            {snap.recipientCategory === 'spouse'
              ? t(flow.stepQuestionKey('pickSpouse'))
              : t(flow.stepQuestionKey('pickDependent'))}
          </ProfileAssistantRow>
          {snap.recipientCategory === 'dependents' || flow.isCommitted('pickSpouse') ? (
            <AnswerRow flow={flow} fieldStep={snap.recipientCategory === 'spouse' ? 'pickSpouse' : 'pickDependent'}>
              {flow.userAnswerForStep(
                snap.recipientCategory === 'spouse' ? 'pickSpouse' : 'pickDependent',
                entry
              )}
            </AnswerRow>
          ) : (
            <ProfileUserAnswerRow>
              {flow.userAnswerForStep('pickDependent', entry)}
            </ProfileUserAnswerRow>
          )}
          {entryHasAmount(entry) ? (
            <>
              <ProfileAssistantRow>{t(flow.stepQuestionKey('enterAmount'))}</ProfileAssistantRow>
              {entryHasAmount(entry) ? (
                <AnswerRow flow={flow} fieldStep="enterAmount">
                  {flow.userAnswerForStep('enterAmount', entry)}
                </AnswerRow>
              ) : null}
            </>
          ) : null}
        </React.Fragment>
      ))}

      {flow.isCommitted('addAnotherDependent') && flow.userAnswerForStep('addAnotherDependent') ? (
        <>
          <ProfileAssistantRow>{t(flow.stepQuestionKey('addAnotherDependent'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="addAnotherDependent">
            {flow.userAnswerForStep('addAnotherDependent')}
          </AnswerRow>
        </>
      ) : null}

      {flow.reviewMode && step === 'review' && (
        <>
          <ProfileAssistantRow>{t('submitDocuments.dependentDeduction.retirementAllowance.reviewSummary')}</ProfileAssistantRow>
          {snap.entries.length > 1 ? (
            <ProfileAssistantRow>
              {t('submitDocuments.dependentDeduction.retirementAllowance.pdfFirstEntryNote')}
            </ProfileAssistantRow>
          ) : null}
        </>
      )}

      {!flow.reviewMode && step !== 'review' && (
        <>
          {step === 'gate' && !flow.isCommitted('gate') ? (
            <>
              <ProfileAssistantRow>{flow.gateQuestion()}</ProfileAssistantRow>
            </>
          ) : null}

          {step === 'recipientType' && !flow.isCommitted('recipientType') ? (
            <>
              <ProfileAssistantRow>{t(flow.stepQuestionKey('recipientType'))}</ProfileAssistantRow>
              {!flow.spouse ? (
                <ProfileAssistantRow>
                  {t('submitDocuments.dependentDeduction.retirementAllowance.noSpouseNotice')}
                </ProfileAssistantRow>
              ) : null}
              {flow.dependents.length === 0 ? (
                <ProfileAssistantRow>
                  {t('submitDocuments.dependentDeduction.retirementAllowance.noDependentsNotice')}
                </ProfileAssistantRow>
              ) : null}
            </>
          ) : null}

          {step === 'pickSpouse' && !flow.isCommitted('pickSpouse') && flow.spouse ? (
            <ProfileAssistantRow>{t(flow.stepQuestionKey('pickSpouse'))}</ProfileAssistantRow>
          ) : null}

          {inDependentLoop && step === 'pickDependent' && !flow.isCommitted('pickDependent') ? (
            <ProfileAssistantRow>{t(flow.stepQuestionKey('pickDependent'))}</ProfileAssistantRow>
          ) : null}

          {step === 'enterAmount' && !flow.isCommitted('enterAmount') ? (
            <ProfileAssistantRow>{t(flow.stepQuestionKey('enterAmount'))}</ProfileAssistantRow>
          ) : null}

          {step === 'addAnotherDependent' && !flow.isCommitted('addAnotherDependent') ? (
            <ProfileAssistantRow>{t(flow.stepQuestionKey('addAnotherDependent'))}</ProfileAssistantRow>
          ) : null}
        </>
      )}
    </>
  );
}
